import type { EditorPlugin, RecordId } from '../../types/aha-editor';
import Annotations from './Annotations';
import { LOCAL_ONLY_ANNOTATION_KEY } from './utils';
import type AhaEditor from '../../AhaEditor';
import { Text, type Descendant, type Node } from 'slate';
import { isAnnotationKey } from '../../types/guards';
import { decodeFragment } from '../../util/fragment';
import History from '../../collaboration/History';

const ANNOTATION_PREFIX = 'annotation_';
const findAnnotationKeysInNode = (node: Node) => {
  let keys: RecordId[] = [];

  if (Text.isText(node)) {
    for (const property of Object.keys(node)) {
      if (property.startsWith(ANNOTATION_PREFIX)) {
        const key = property.substring(ANNOTATION_PREFIX.length);
        if (isAnnotationKey(key) && key !== LOCAL_ONLY_ANNOTATION_KEY) {
          keys.push(key);
        }
      }
    }
    return keys;
  }

  for (const child of node.children) {
    keys = keys.concat(findAnnotationKeysInNode(child));
  }
  return keys;
};

const findAnnotationKeysInFragment = (fragment: Descendant[]): RecordId[] => {
  let keys: RecordId[] = [];

  for (const node of fragment) {
    keys = keys.concat(findAnnotationKeysInNode(node));
  }

  return keys;
};
const AnnotationsPastePlugin: EditorPlugin = {
  name: 'Annotations  paste plugin',
  handlers: {
    onPaste: (event: ClipboardEvent, ahaEditor: AhaEditor): boolean => {
      const { clipboardData } = event;
      if (!clipboardData) return false;

      const encodedFragment = clipboardData.getData(
        'application/x-aha-v2-fragment'
      );
      if (encodedFragment === '') return false;

      const fragment = decodeFragment(encodedFragment);
      if (!Array.isArray(fragment)) return false;

      const keysInFragment: RecordId[] = findAnnotationKeysInFragment(fragment);

      // The paste handlers run before the content is added to the
      // document, so we need to wait until the next tick to
      // process the annotations.
      requestAnimationFrame(() => {
        const { editor } = ahaEditor;
        const annotationsObject = editor.metadata?.annotations;
        if (!annotationsObject) return;

        History.withUndoMerging(editor, () => {
          for (const key of keysInFragment) {
            const annotation = annotationsObject[key];
            if (annotation?.wasCut) {
              Annotations.pasteAnnotation(editor, annotation);
            }
            Annotations.cleanupAnnotationMarks(editor, key);
          }
        });
      });

      return false;
    },
  },
};

export default AnnotationsPastePlugin;
